import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useResponseById } from "@/hooks/response-by-id.hook";
import { useUpdateAdditionalCommentsMutation } from "@/hooks/update-additional-comments.hook";
import { useQuestion } from "@/store/question.context";
import { DimensionId } from "@/types/questionnaries";
import { FC, useEffect, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

type AdditionalCommentsProps = {
  disabled?: boolean;
};

const AdditionalComments: FC<AdditionalCommentsProps> = (props) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { responseId, quizId, setLastSavedTime, setIsSaving } = useQuestion();

  const { data: userProfile } = useCurrentUserProfile();

  const { data: question, dataUpdatedAt: questionUpdatedAt } = useResponseById({
    companyId: userProfile?.companyId || "",
    quizId,
    responseId: responseId,
  });

  const mutation = useUpdateAdditionalCommentsMutation();

  const debounced = useDebouncedCallback((value) => {
    setIsSaving(true);

    mutation.mutate(
      {
        companyId: userProfile?.companyId || "",
        quizId,
        responseId: question?.responseId || "",
        additionalComments: value,
        dimension: question?.dimension as DimensionId,
        topic: question?.topic || "",
      },
      {
        onSuccess: () => {
          setLastSavedTime(new Date());
        },
        onSettled: () => {
          setIsSaving(false);
        },
      },
    );
  }, 400);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = question?.additionalComments || "";
    }
  }, [questionUpdatedAt]);

  return question?.allowAdditionalComments ? (
    <>
      <Separator />

      <div className="grid w-full max-w-sm items-center gap-1.5">
        <Label htmlFor={`additional-comments-${question.id}`}>
          Comentarios adicionales
        </Label>

        <Textarea
          id={`additional-comments-${question.id}`}
          ref={inputRef}
          defaultValue={question.additionalComments}
          onChange={(e) => debounced(e.target.value)}
          rows={3}
          disabled={props.disabled}
          placeholder="Añade aquí cualquier detalle o contexto adicional relevante para esta sección"
        />
      </div>
    </>
  ) : null;
};

export default AdditionalComments;
